/**
 * EAN utility functions.
 *
 * medpdb2 stores EANs in a masked format: a '5' is inserted at position 3,
 * turning a 13-digit EAN into a 14-digit masked string. Every display surface
 * must call eanUnmask() before showing an EAN to users, and search inputs
 * must be passed through eanMask() before querying Typesense.
 */

/**
 * Unmask an EAN from medpdb2's obfuscated storage format.
 *
 * The masking inserts '5' at position 3 of a 13-digit EAN, producing a
 * 14-digit string. This function reverses that transformation.
 *
 * Returns the input unchanged if it doesn't match the masked pattern
 * (not all-digits, fewer than 6 chars, or position 3 isn't '5').
 */
export function eanUnmask(masked: string): string {
  if (!/^\d{6,}$/.test(masked)) return masked;
  if (masked[3] !== '5') return masked;
  return masked.slice(4, 6) + masked.slice(0, 3) + masked.slice(6);
}

/**
 * Deterministic digit rearrangement used across the codebase to obfuscate stored EANs.
 * Inverse of eanUnmask.
 *
 * Returns the input unchanged if it is not a run of >=5 digits. Callers must pass
 * a string; this function does not null-guard.
 */
export function eanMask(ean: string): string {
  if (!/^\d{5,}$/.test(ean)) return ean;
  return ean.slice(2, 5) + '5' + ean.slice(0, 2) + ean.slice(5);
}

export type EanQueryKind =
  | 'textual' // not a numeric EAN/NTIN query; run regular text search
  | 'eanPrefix' // numeric, length 5-13; apply eanMask and use as prefix search
  | 'maskedExact'; // numeric, length 14; already a masked EAN

export interface EanQueryClassification {
  readonly kind: EanQueryKind;
  readonly rawTerm: string;
  readonly maskedTerm?: string;
}

/**
 * Classifies a search term to decide how (or whether) to transform it
 * before sending to Typesense for EAN search.
 * Pure function; safe in SSR.
 *
 * NTIN (6-digit) search is postponed — pending a backend decision on whether
 * to enable Typesense infix on EAN or add a dedicated NTIN field. Length-6
 * numeric inputs currently flow through eanPrefix alongside other lengths;
 * masked prefix semantics make them unlikely to match, which is acceptable
 * until the NTIN approach is chosen.
 */
export function classifyEanQuery(term: string): EanQueryClassification {
  const raw = (term ?? '').trim();
  if (!/^\d+$/.test(raw)) return { kind: 'textual', rawTerm: raw };
  if (raw.length < 5) return { kind: 'textual', rawTerm: raw };
  if (raw.length === 14) return { kind: 'maskedExact', rawTerm: raw, maskedTerm: raw };
  return { kind: 'eanPrefix', rawTerm: raw, maskedTerm: eanMask(raw) };
}
